import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaCode,
  FaMobileAlt,
  FaGlobe,
  FaPaintBrush,
  FaSearch,
  FaServer,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";

import Header from "../Templete/Header";
import Footer from "../Templete/Footer";
import "./ServicePages.css";

const services = [
  {
    title: "Software Development",
    icon: FaCode,
    description:
      "Custom software built around your business processes, users, and operational requirements.",
    features: [
      "Business and internal management applications",
      "Customer and partner portals",
      "Workflow automation and reporting tools",
    ],
  },
  {
    title: "Mobile App Development",
    icon: FaMobileAlt,
    description:
      "Mobile applications that help customers, partners, and teams access services wherever they work.",
    features: [
      "Android and cross-platform applications",
      "Backend and service integrations",
      "Clear navigation and mobile user journeys",
    ],
  },
  {
    title: "Website Design & Development",
    icon: FaGlobe,
    description:
      "Responsive websites that communicate your business clearly and support your customer enquiries.",
    features: [
      "Company and product websites",
      "Responsive layouts for different devices",
      "Forms, content, and service integrations",
    ],
  },
  {
    title: "UI/UX & Graphics Design",
    icon: FaPaintBrush,
    description:
      "Thoughtful interface design and visual communication for your applications, brand, and digital presence.",
    features: [
      "User journeys, wireframes, and interface design",
      "Application and website visual design",
      "Brand graphics and marketing creatives",
    ],
  },
  {
    title: "SEO Services",
    icon: FaSearch,
    description:
      "Improve how your website is structured, understood, and discovered through search.",
    features: [
      "Technical and on-page SEO reviews",
      "Content structure and keyword planning",
      "Search performance monitoring",
    ],
  },
  {
    title: "Technology Service Provider (TSP)",
    icon: FaServer,
    description:
      "Technical development and integration support for businesses building connected digital platforms.",
    features: [
      "API and backend infrastructure development",
      "Third-party system integrations",
      "Application maintenance and technical support",
    ],
  },
];

const TechnologyDigitalSolutions = () => {
  useEffect(() => {
    document.title = "Technology & Digital Solutions | Formax IT Solutions";
  }, []);

  return (
    <>
      <Header />

      <main className="fit-services">
        <section className="fit-services__hero">
          <div className="fit-services__container">
            <nav className="fit-services__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Technology & Digital Solutions</span>
            </nav>

            <span className="fit-services__eyebrow">
              DEVELOP · DESIGN · CONNECT
            </span>

            <h1>
              Technology &<span>Digital Solutions</span>
            </h1>

            <p>
              From your business website to the systems behind your operations,
              we develop digital solutions around the work you need to do.
            </p>

            <a href="#digital-services" className="fit-services__button">
              Explore our services
              <FaArrowRight aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="fit-services__section">
          <div className="fit-services__container fit-services__intro">
            <div>
              <span className="fit-services__eyebrow">
                BUILT FOR YOUR BUSINESS
              </span>
              <h2>Turn your requirements into a useful digital product.</h2>
            </div>
            <div>
              <p>
                Every business has its own processes, customers, and priorities.
                We start by understanding these requirements before defining the
                application, interface, or integration.
              </p>
              <p>
                Our services connect design and development, helping you move
                from an initial idea to a working solution with a clear scope
                and practical implementation.
              </p>
            </div>
          </div>
        </section>

        <section
          id="digital-services"
          className="fit-services__section fit-services__section--soft"
          aria-labelledby="digital-services-title"
        >
          <div className="fit-services__container">
            <div className="fit-services__heading">
              <span className="fit-services__eyebrow">OUR SERVICES</span>
              <h2 id="digital-services-title">
                Build your next digital capability.
              </h2>
              <p>
                Choose the support you need for your website, application,
                design, or connected infrastructure.
              </p>
            </div>

            <div className="fit-services__grid">
              {services.map((service) => {
                const Icon = service.icon;

                return (
                  <article className="fit-services__card" key={service.title}>
                    <span className="fit-services__icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="fit-services__list">
                      {service.features.map((feature) => (
                        <li key={feature}>
                          <FaCheck aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="fit-services__section">
          <div className="fit-services__container">
            <div className="fit-services__heading">
              <span className="fit-services__eyebrow">OUR APPROACH</span>
              <h2>A clear path from requirements to release.</h2>
            </div>

            <ol className="fit-services__steps">
              {[
                [
                  "Understand",
                  "Discuss your goals, users, existing systems, and priorities.",
                ],
                [
                  "Plan & design",
                  "Define the scope, user journeys, and technical approach.",
                ],
                [
                  "Build & validate",
                  "Develop the solution and check important functionality.",
                ],
                [
                  "Launch & maintain",
                  "Prepare deployment and agree ongoing support requirements.",
                ],
              ].map(([title, text], index) => (
                <li key={title}>
                  <span aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="fit-services__section fit-services__section--soft">
          <div className="fit-services__container">
            <div className="fit-services__cta">
              <div>
                <span className="fit-services__eyebrow">LET’S CONNECT</span>
                <h2>Tell us what you want to build.</h2>
                <p>
                  Share your requirements for a website, application, or
                  integration with our team.
                </p>
              </div>
              <Link to="/contact-us" className="fit-services__button">
                Contact us <FaArrowRight aria-hidden="true" />
              </Link>
            </div>

            <Link to="/payments-e-governance" className="fit-services__related">
              Explore Payments & E-Governance Services
              <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default TechnologyDigitalSolutions;
